import React from 'react'
import { Control, Controller, Path, UseFormSetValue } from 'react-hook-form'
import { Divider, Dropdown, makeStyles, mergeClasses, Option, Text, tokens } from '@fluentui/react-components'
import { Field } from './Field'
import { t } from '../../../i18n/i18n'
import { Dismiss20Regular } from '@fluentui/react-icons'
import { ConditionalTooltip } from '../other/ConditionalTooltip'
import { ProtectedAssetGoal, ProtectedAssetGoalType } from '../../../api/schemas/schema'

const useStyles = makeStyles({
   dismissIcon: {
      padding: `9px 0`,
      marginRight: '4px',
   },
   sectionHeader: {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      color: tokens.colorNeutralForeground3,
      display: 'block',
   },
   marginTop: {
      marginTop: tokens.spacingVerticalS,
   },
})

type ControlledProtectedAssetGoalDropdownProps<T> = {
   name: Path<T>
   control: Control<T, any>
   items: any[]
   label: string | JSX.Element
   customLabel?: JSX.Element
   disabled?: boolean
   required?: boolean
   setValue: UseFormSetValue<any>
   setValueString: string
   showTooltip?: boolean
   tooltipMsg?: string
}
export const ControlledProtectedAssetGoalDropdown = <T extends {}>({
   name,
   items,
   control,
   label,
   disabled,
   required,
   setValue,
   setValueString,
   showTooltip,
   tooltipMsg,
}: ControlledProtectedAssetGoalDropdownProps<T>) => {
   const classes = useStyles()

   const protectedAssets = items.filter((i) => i.type === ProtectedAssetGoalType.ProtectedAsset) as ProtectedAssetGoal[]
   const organisationalGoals = items.filter((i) => i.type === ProtectedAssetGoalType.OrganisationalGoal) as ProtectedAssetGoal[]

   return (
      <Controller
         name={name}
         control={control}
         render={({ field: { value, onChange } }) => {
            const typedValue = value as { id: string; name: string } | null
            const selectedId = typedValue?.id ?? null
            const selectedName = typedValue?.name ?? ''
            return (
               <Field label={label} required={required ?? false}>
                  <ConditionalTooltip show={showTooltip} content={tooltipMsg}>
                     <Dropdown
                        clearable
                        clearButton={<Dismiss20Regular className={classes.dismissIcon} />}
                        disabled={disabled}
                        selectedOptions={selectedId ? [selectedId] : []}
                        value={selectedId ? selectedName : ''}
                        onOptionSelect={(_, data) => {
                           const picked = items.find((i) => i.id === data.optionValue) ?? null
                           onChange(picked)
                           setValue(setValueString, picked?.id ?? null)
                        }}
                     >
                        <Text  weight='semibold' className={classes.sectionHeader}>
                           {t('ProtectedAssets')}
                        </Text>
                        <Divider />
                        {protectedAssets.map((p) => (
                           <Option key={p.id} value={p.id}>
                              {p.name}
                           </Option>
                        ))}
                        <Text weight='semibold'  className={mergeClasses(classes.sectionHeader, classes.marginTop)}>{t('OrganisationalGoals')}</Text>
                        <Divider />
                        {organisationalGoals.map((p) => (
                           <Option key={p.id} value={p.id}>
                              {p.name}
                           </Option>
                        ))}
                     </Dropdown>
                  </ConditionalTooltip>
               </Field>
            )
         }}
      />
   )
}
